// Stateless native exploration geometry, shared by browser planners and the Node migration tool.
/** @typedef {import('../../includes/js-src/encounter-creation-engine/ft3d-exploration').Ft3dExplorationCell} Cell */
/** @typedef {import('../../includes/js-src/encounter-creation-engine/ft3d-exploration').Ft3dExplorationBox} Box */
/** @typedef {import('../../includes/js-src/encounter-creation-engine/ft3d-exploration').Ft3dExplorationShape} Shape */

/** @type {'ft.ft3d.exploration-cell.v1'} */
export const FT3D_EXPLORATION_CELL_SCHEMA = 'ft.ft3d.exploration-cell.v1';
/** @type {'ft.ft3d.exploration-registry.v1'} */
export const FT3D_EXPLORATION_REGISTRY_SCHEMA = 'ft.ft3d.exploration-registry.v1';

/** Fixed three-decimal wire form for durable cell identities. @param {number} value */
export function canonicalExplorationNumber(value) {
  if (!Number.isFinite(value)) throw new TypeError('Exploration cell coordinates must be finite.');
  const rounded = Math.sign(value) * Math.round(Math.abs(value) * 1000 + Number.EPSILON) / 1000;
  return (Object.is(rounded, -0) || Math.abs(rounded) < 0.0005 ? 0 : rounded).toFixed(3);
}

/** Stable JSON bytes; property order is intentional.
 * @param {Omit<Cell, 'cellKey'>} cell
 * @param {number} cellSizeFt
 */
export function canonicalExplorationCellJson(cell, cellSizeFt = 5) {
  if (!cell.roomId.trim()) throw new TypeError('Exploration cells require a room identity.');
  return JSON.stringify({
    schema: FT3D_EXPLORATION_CELL_SCHEMA, roomId: cell.roomId.trim(),
    x: { min: canonicalExplorationNumber(cell.x.min), max: canonicalExplorationNumber(cell.x.max) },
    y: { min: canonicalExplorationNumber(cell.y.min), max: canonicalExplorationNumber(cell.y.max) },
    floor: canonicalExplorationNumber(cell.floor), ceiling: canonicalExplorationNumber(cell.ceiling),
    cellSizeFt: canonicalExplorationNumber(cellSizeFt),
  });
}

/** @param {string} value */
export async function sha256Hex(value) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

/** @param {Box} box @param {number} cellSizeFt @returns {Array<Omit<Cell, 'cellKey'>>} */
export function explorationVisibilityCells(box, cellSizeFt = 5) {
  if (!box.id.trim() || box.x.max <= box.x.min || box.y.max <= box.y.min
    || !Number.isFinite(cellSizeFt) || cellSizeFt <= 0) return [];
  const cells = [];
  let column = 0;
  for (let x = box.x.min; x < box.x.max - 0.0001; x += cellSizeFt, column += 1) {
    let row = 0;
    for (let y = box.y.min; y < box.y.max - 0.0001; y += cellSizeFt, row += 1) {
      cells.push({ id: `${box.id}:${column}:${row}`, roomId: box.id,
        x: { min: x, max: Math.min(box.x.max, x + cellSizeFt) },
        y: { min: y, max: Math.min(box.y.max, y + cellSizeFt) }, floor: box.floor, ceiling: box.ceiling });
    }
  }
  return cells;
}

/** Native registry; no persistence or visibility judgments.
 * @param {Shape} shape
 * @param {number} cellSizeFt
 * @returns {Promise<{schema: typeof FT3D_EXPLORATION_REGISTRY_SCHEMA, cellSizeFt: number, geometryRevision: string, cells: Cell[]}>}
 */
export async function buildExplorationCellRegistry(shape, cellSizeFt = 5) {
  if (!Number.isFinite(cellSizeFt) || cellSizeFt <= 0) throw new TypeError('Exploration cell size must be positive and finite.');
  const cells = [];
  for (const box of shape.boxes) {
    for (const cell of explorationVisibilityCells(box, cellSizeFt)) {
      cells.push({ ...cell, cellKey: await sha256Hex(canonicalExplorationCellJson(cell, cellSizeFt)) });
    }
  }
  cells.sort((left, right) => left.cellKey.localeCompare(right.cellKey));
  const revisionJson = JSON.stringify({ schema: FT3D_EXPLORATION_REGISTRY_SCHEMA,
    cellSizeFt: canonicalExplorationNumber(cellSizeFt), cellKeys: cells.map(cell => cell.cellKey) });
  return { schema: FT3D_EXPLORATION_REGISTRY_SCHEMA, cellSizeFt,
    geometryRevision: await sha256Hex(revisionJson), cells };
}
